import './HeaderSelect.sass';

import React, { FC, useEffect, useMemo } from 'react';
import { animated, useSpring } from '@react-spring/web';

import { HeaderSelectProps } from '$uikit/HeaderSelect/HeaderSelect.interface';
import { Header } from '$uikit';
import { classNames as cn, getCssVar } from '$utils';
import { IcAngleUp16, IcCheck28 } from '$assets';
import { useResize } from '$hooks';

export const HeaderSelect: FC<HeaderSelectProps> = ({
  children,
  value,
  onChange,
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { height } = useResize();

  const optionsHeight = 56;

  const options = useMemo(
    () =>
      React.Children.toArray(children)
        .map(
          (child) =>
            React.isValidElement(child) && {
              label: child.props.children,
              value: child.props.value,
            },
        )
        .filter(Boolean),
    [children],
  );

  const [label, currentValue] = useMemo(() => {
    // @ts-ignore
    const option = options.find((option) => option.value === value);
    return option ? [option.label, option.value] : [null, null];
  }, [options, value]);

  const maxHeight = Math.min(optionsHeight * options.length, height - 56 * 2);

  const style = useSpring({
    height: isOpen ? maxHeight : 0,
    config: {
      // tension: 250,
      // friction: 25,
    },
  });

  const handleSelect = (value) => {
    onChange(value);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.HeaderSelect')) {
        setIsOpen(false);
      }
    };

    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('click', handleClick);
    };
  }, [isOpen]);

  return (
    <Header className={cn('HeaderSelect', { isOpen })} {...props}>
      <div className="HeaderSelect__wrapper">
        <div className="HeaderSelect__title" onClick={() => setIsOpen(!isOpen)}>
          <div className="HeaderSelect__title__value">{label}</div>
          <div className="HeaderSelect__title__arrow">
            <IcAngleUp16 />
          </div>
        </div>
        <animated.div style={{ height: style.height }} className="HeaderSelect__options">
          {React.Children.map(children, (child) => {
            return React.isValidElement(child) ? (
              <div
                key={child.props.value}
                onClick={() => handleSelect(child.props.value)}
                className="HeaderSelect__options__option"
              >
                <div className="HeaderSelect__options__option__value">
                  {child.props.children}
                </div>
                {child.props.value === currentValue && (
                  <div className="HeaderSelect__options__option__check">
                    <IcCheck28 />
                  </div>
                )}
              </div>
            ) : null;
          })}
        </animated.div>
      </div>
    </Header>
  );
};
